import { useState, type ReactNode } from 'react';
import { Check, Eye, History, Undo2, X } from 'lucide-react';

import {
  isStaleRefusal,
  useDecideStorefrontChanges,
  useRevertStorefront,
  useStorefrontPreviewToken,
  type AdminStorefrontDetail,
  type StorefrontClaimFinding,
  type StorefrontVersion,
} from '@/lib/api/storefrontAdminHooks';
import { cn } from '@/lib/cn';

import { fieldLabel, productNameMap, readableNote } from './storeChanges';
import { ChangeList } from './storeDiff';
import { dateTime, refusalMessage, safeHttpUrl } from './storefrontFormat';
import { ActionButton, ConfirmDialog, Notice, Panel, Pill, textareaClass } from './ui';

/*
 * Approved-store edits (contract D1). An owner of a live or paused store
 * edits a draft and publishes it: clean edits go live at once, flagged ones
 * (claims flags, a new photo or logo) wait here while the live store keeps
 * its last approved version. The admin reads the diff and the flags, can
 * preview the change, and approves (publishes) or rejects it. Every
 * approval is a snapshot; "Revert to last approved" publishes the newest
 * one again without pausing the store.
 */

const REASON_LABEL: Record<string, string> = {
  claims_flags: 'automated flags in the new copy',
  new_copy: 'new wording',
  new_image: 'a new image',
  // Read after "It waits for review because of …".
  store_in_review: "the store's own open review",
  store_review_required: "the store's Every change review policy",
};

type ChangeReason = { reason: string; field?: string | null; count?: number | null };

function reasonText(reason: ChangeReason, productNames: Record<string, string>): string {
  if (reason.reason === 'claims_flags') {
    const n = reason.count ?? 0;
    return n ? `${n} automated flag${n === 1 ? '' : 's'}` : 'automated flags';
  }
  if (reason.reason === 'new_copy') {
    const n = reason.count ?? 0;
    return n > 1 ? `new wording in ${n} places` : REASON_LABEL.new_copy;
  }
  if (reason.reason === 'new_image') {
    return reason.field ? `a new image (${fieldLabel(reason.field, productNames).label})` : 'a new image';
  }
  return REASON_LABEL[reason.reason] ?? reason.reason.replace(/_/g, ' ');
}

/** Why a change waits, as one phrase: "2 automated flags and new wording",
 *  "a new image (Logo), new wording and 1 automated flag". */
export function reasonsSentence(reasons: ChangeReason[], productNames: Record<string, string>): string {
  const parts = [...new Set((reasons.length ? reasons : [{ reason: 'claims_flags' }]).map((r) => reasonText(r, productNames)))];
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The owner's draft holds exactly the change waiting for review (they have
 *  not edited since sending it). */
function sameChanges(
  a: { field: string; old: unknown; new: unknown }[],
  b: { field: string; old: unknown; new: unknown }[],
): boolean {
  const key = (list: typeof a) =>
    JSON.stringify(
      [...list]
        .map((c) => [c.field, c.old ?? null, c.new ?? null])
        .sort((x, y) => String(x[0]).localeCompare(String(y[0]))),
    );
  return key(a) === key(b);
}

/** One field's flags: its text with every flagged phrase marked, and a
 *  "Must change" / "Check" tag per phrase. */
function FlagLine({
  field,
  flags,
  productNames,
}: {
  field: string;
  flags: StorefrontClaimFinding[];
  productNames: Record<string, string>;
}) {
  const text = flags.find((f) => f.text)?.text ?? '';
  const placed = flags
    .filter((f) => f.start >= 0 && f.end > f.start && f.end <= text.length)
    .sort((a, b) => a.start - b.start)
    .filter((f, i, all) => i === 0 || f.start >= all[i - 1].end);
  const parts: ReactNode[] = [];
  let at = 0;
  placed.forEach((f, i) => {
    if (f.start > at) parts.push(text.slice(at, f.start));
    parts.push(
      <mark
        key={i}
        title={f.severity === 'block' ? 'Must change' : 'Check'}
        className={cn('text-ink px-2', f.severity === 'block' ? 'bg-[#FECACA]' : 'bg-[#FDE68A]')}
      >
        {text.slice(f.start, f.end)}
      </mark>,
    );
    at = f.end;
  });
  if (at < text.length) parts.push(text.slice(at));
  return (
    <li className="flex flex-col gap-6 py-10 border-b border-line-subtle last:border-b-0">
      <span className="type-label-xs text-ink-muted normal-case">
        {fieldLabel(field.replace(/\.items\.\d+\.[qa]$/, '.items'), productNames).label}
      </span>
      <span className="type-body-sm text-ink break-words leading-130">
        {placed.length > 0 ? parts : flags.map((f) => `“${f.phrase}”`).join(', ')}
      </span>
      <span className="flex flex-wrap items-center gap-6">
        {flags.map((f, i) => (
          <Pill key={i} tone={f.severity === 'block' ? 'danger' : 'warning'} className="normal-case">
            {f.severity === 'block' ? 'Must change' : 'Check'}: {f.phrase}
          </Pill>
        ))}
      </span>
    </li>
  );
}

function usePreview(storeId: string) {
  const preview = useStorefrontPreviewToken(storeId);
  const [error, setError] = useState<string | null>(null);
  async function open(content: 'draft' | 'pending') {
    setError(null);
    // Opened inside the click so the popup is allowed; cut off from this
    // tab before it navigates.
    const tab = window.open('', '_blank');
    try {
      const res = await preview.mutateAsync(content);
      const url = safeHttpUrl(res.url);
      if (!url) throw new Error('The preview link is not a web address.');
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      }
    } catch (err) {
      tab?.close();
      setError(refusalMessage(err));
    }
  }
  return { open, pending: preview.isPending, error };
}

export function PendingChangesPanel({ store }: { store: AdminStorefrontDetail }) {
  const pending = store.pending_changes ?? null;
  const draft = store.draft ?? null;
  const names = productNameMap(store);
  const decide = useDecideStorefrontChanges(store.id);
  const preview = usePreview(store.id);
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [note, setNote] = useState('');
  const [read, setRead] = useState(false);
  const [flagsRead, setFlagsRead] = useState(false);
  const [imagesChecked, setImagesChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDraft, setShowDraft] = useState(false);

  if (!pending && !draft) return null;
  // A draft that only repeats the change waiting for review is not news.
  const draftIsPending = Boolean(pending && draft && sameChanges(draft.changes, pending.changes));

  const flags = pending?.flags ?? [];
  const blocking = flags.filter((f) => f.severity === 'block').length;
  const newImages = (pending?.reasons ?? []).filter((r) => r.reason === 'new_image');

  function open(action: 'approve' | 'reject') {
    setRead(false);
    setFlagsRead(false);
    setImagesChecked(false);
    setError(null);
    decide.reset();
    setDialog(action);
  }

  async function submit() {
    if (!pending || !dialog) return;
    setError(null);
    try {
      await decide.mutateAsync({
        action: dialog,
        note: note.trim() || null,
        revision_id: pending.id,
      });
      setNote('');
      setDialog(null);
    } catch (err) {
      setError(
        isStaleRefusal(err)
          ? `Not sent. ${refusalMessage(err)} Your note is kept.`
          : refusalMessage(err),
      );
    }
  }

  return (
    <Panel
      title={pending ? 'Changes waiting for review' : 'Unpublished edits'}
      className={pending ? 'border-info' : undefined}
      aside={
        pending ? (
          <>
            {flags.length > 0 && (
              <Pill tone="warning" className="normal-case">
                {flags.length} flag{flags.length === 1 ? '' : 's'}
                {blocking ? ` · ${blocking} must change` : ''}
              </Pill>
            )}
            <Pill tone="info" className="normal-case">
              Live store unchanged
            </Pill>
          </>
        ) : (
          <Pill className="normal-case">Not live</Pill>
        )
      }
    >
      <div className="flex flex-col gap-16">
        {pending && (
          <>
            <Notice tone="info">
              {pending.submitted_by ?? 'The owner'} published{' '}
              {pending.changes.length === 1 ? 'a change' : `${pending.changes.length} changes`} on{' '}
              {dateTime(pending.submitted_at)}. It waits for review because of{' '}
              {reasonsSentence(pending.reasons, names)}. Until you approve it, patients see the last
              approved version.
            </Notice>

            {flags.length > 0 && (
              <div className="flex flex-col gap-6">
                <span className="type-label-xs text-ink-muted">
                  Automated flags in the new copy · always read every changed line
                </span>
                <ul className="flex flex-col border border-line-subtle px-12">
                  {[...new Set(flags.map((f) => f.field))].map((field) => (
                    <FlagLine
                      key={field}
                      field={field}
                      flags={flags.filter((f) => f.field === field)}
                      productNames={names}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-6">
              <span className="type-label-xs text-ink-muted">What changes</span>
              {pending.changes.length === 0 ? (
                <p className="type-body-sm text-ink-muted">No field changes recorded.</p>
              ) : (
                <ChangeList changes={pending.changes} productNames={names} />
              )}
            </div>

            <div className="flex flex-wrap items-center gap-8">
              <ActionButton tone="primary" onClick={() => open('approve')}>
                <Check size={14} aria-hidden /> Approve and publish
              </ActionButton>
              <ActionButton tone="danger" onClick={() => open('reject')}>
                <X size={14} aria-hidden /> Reject changes
              </ActionButton>
              <ActionButton onClick={() => preview.open('pending')} disabled={preview.pending}>
                <Eye size={14} aria-hidden /> {preview.pending ? 'Opening…' : 'Preview the change'}
              </ActionButton>
            </div>
            {preview.error && <Notice tone="danger">{preview.error}</Notice>}
          </>
        )}

        {draftIsPending && (
          <p className="type-body-sm text-ink-muted pt-12 border-t border-line-subtle">
            The owner has not edited the store since sending this for review.
          </p>
        )}
        {draft && !draftIsPending && (
          <div className={cn('flex flex-col gap-8', pending && 'pt-12 border-t border-line-subtle')}>
            <span className="type-body-sm text-ink-secondary leading-130">
              The owner has unpublished edits{draft.draft_updated_by ? ` by ${draft.draft_updated_by}` : ''}{' '}
              (last saved {dateTime(draft.draft_updated_at)}): {draft.changes.length} change
              {draft.changes.length === 1 ? '' : 's'}
              {pending
                ? ' against the live store, edited after the change above was sent for review. Only the change above waits for review; the later edits are not live.'
                : ', not live and not sent for review.'}
            </span>
            <div className="flex flex-wrap items-center gap-8">
              {draft.changes.length > 0 && (
                <ActionButton tone="ghost" className="h-30 px-10" onClick={() => setShowDraft((v) => !v)}>
                  {showDraft ? 'Hide the edits' : 'Show the edits'}
                </ActionButton>
              )}
              <ActionButton
                tone="ghost"
                className="h-30 px-10"
                onClick={() => preview.open('draft')}
                disabled={preview.pending}
              >
                <Eye size={13} aria-hidden /> Preview the draft
              </ActionButton>
            </div>
            {showDraft && <ChangeList changes={draft.changes} productNames={names} />}
            {!pending && preview.error && <Notice tone="danger">{preview.error}</Notice>}
          </div>
        )}
      </div>

      {pending && (
        <>
          <ConfirmDialog
            open={dialog === 'approve'}
            onOpenChange={(next) => !next && setDialog(null)}
            title="Approve and publish changes"
            description={`The change goes live on ${store.name} at once and becomes its approved version.`}
            confirmLabel="Approve and publish"
            pending={decide.isPending}
            confirmDisabled={
              !read || (flags.length > 0 && !flagsRead) || (newImages.length > 0 && !imagesChecked)
            }
            error={error}
            onConfirm={submit}
          >
            {flags.length > 0 && (
              <label className="flex items-start gap-8 border border-warning bg-warning-wash px-10 py-8 type-body-sm text-ink">
                <input type="checkbox" checked={flagsRead} onChange={(e) => setFlagsRead(e.target.checked)} className="mt-2" />
                <span>
                  I read the {flags.length === 1 ? 'flagged phrase' : `${flags.length} flagged phrases`}
                  {blocking ? ', including copy an owner save would refuse,' : ''} and none makes a
                  medical or results claim patients could rely on.
                </span>
              </label>
            )}
            {newImages.length > 0 && (
              <label className="flex items-start gap-8 border border-warning bg-warning-wash px-10 py-8 type-body-sm text-ink">
                <input
                  type="checkbox"
                  checked={imagesChecked}
                  onChange={(e) => setImagesChecked(e.target.checked)}
                  className="mt-2"
                />
                <span>
                  I looked at the new {newImages.length === 1 ? 'image' : 'images'}: no before/after or
                  results imagery, no white coats or clinical settings, nothing that suggests a medical
                  endorsement.
                </span>
              </label>
            )}
            <label className="flex items-start gap-8 border border-line bg-raised px-10 py-8 type-body-sm text-ink">
              <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="mt-2" />
              <span>
                I read every changed line, and it makes no outcome, safety or endorsement claim.
              </span>
            </label>
            <label className="flex flex-col gap-6">
              <span className="type-label-xs text-ink-muted">Note to the owner (optional)</span>
              <textarea className={textareaClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
            </label>
          </ConfirmDialog>

          <ConfirmDialog
            open={dialog === 'reject'}
            onOpenChange={(next) => !next && setDialog(null)}
            title="Reject changes"
            tone="danger"
            description="Nothing is published: the live store keeps its approved version, and the owner keeps their draft to fix and publish again."
            confirmLabel="Reject changes"
            pending={decide.isPending}
            confirmDisabled={!note.trim()}
            error={error}
            onConfirm={submit}
          >
            <label className="flex flex-col gap-6">
              <span className="type-label-xs text-ink-muted">Note to the owner (required)</span>
              <textarea
                className={textareaClass}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
                placeholder="What has to change, e.g. “The new hero subheadline promises results; reword it.”"
                autoFocus
              />
              <span className="type-body-sm text-ink-muted leading-130">
                The owner sees this in their console. Plain text; no patient details.
              </span>
            </label>
          </ConfirmDialog>
        </>
      )}
    </Panel>
  );
}

const VERSION_LABEL: Record<string, string> = {
  baseline: 'Approved (before versioning)',
  approved: 'Approved',
  published: 'Published by the owner',
  reverted: 'Reverted',
};

/** The newest approved snapshot, and whether the store shows something
 *  newer (a clean publish since, or the revert target is not the latest). */
export function revertTarget(store: AdminStorefrontDetail): {
  target: StorefrontVersion | null;
  needed: boolean;
} {
  const versions = [...(store.versions ?? [])].sort((a, b) => b.version - a.version);
  const preview = store.revert_preview ?? null;
  const target =
    (preview ? versions.find((v) => v.version === preview.version) : null) ??
    versions.find((v) => v.kind === 'approved' || v.kind === 'baseline') ??
    null;
  // The backend's preview says exactly what a revert would put back; an
  // older backend's detail is read from the versions.
  if (preview) return { target, needed: preview.restores.length > 0 };
  const latest = versions[0] ?? null;
  return { target, needed: Boolean(target && latest && latest.id !== target.id && latest.kind !== 'reverted') };
}

export function RevertButton({ store }: { store: AdminStorefrontDetail }) {
  const revert = useRevertStorefront(store.id);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { target, needed } = revertTarget(store);
  if (!store.versions || store.versions.length === 0) return null;
  if (store.status !== 'live' && store.status !== 'paused') return null;
  const names = productNameMap(store);
  const preview = store.revert_preview ?? null;
  // What a revert undoes: the backend's preview, else (an older backend) the
  // field changes recorded since that version.
  const since =
    !preview && target
      ? store.events
          .filter(
            (e) =>
              e.kind === 'change' &&
              e.field &&
              !e.action.startsWith('payout_') &&
              Date.parse(e.created_at) > Date.parse(target.created_at),
          )
          .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
      : [];

  return (
    <>
      <ActionButton
        onClick={() => {
          setError(null);
          revert.reset();
          setOpen(true);
        }}
        disabled={!needed}
        title={
          !target
            ? 'No approved version to go back to'
            : needed
              ? undefined
              : preview && preview.not_restored.length > 0
                ? 'The live store already shows its last approved version; product, price and promo edits are not part of a version'
                : 'The live store already shows its last approved version'
        }
      >
        <Undo2 size={14} aria-hidden /> Revert to last approved
      </ActionButton>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => !next && setOpen(false)}
        title="Revert to last approved"
        tone="danger"
        description={
          target
            ? `${store.name} goes back to version ${target.version}, ${VERSION_LABEL[target.kind]?.toLowerCase() ?? target.kind} on ${dateTime(target.created_at)}${target.actor ? ` by ${target.actor}` : ''}. The store stays ${store.status}; the owner keeps their draft, and a change waiting for review stays waiting.`
            : undefined
        }
        confirmLabel="Revert"
        pending={revert.isPending}
        error={error}
        onConfirm={async () => {
          setError(null);
          try {
            await revert.mutateAsync({ note: note.trim() || null });
            setNote('');
            setOpen(false);
          } catch (err) {
            setError(refusalMessage(err));
          }
        }}
      >
        {preview ? (
          <RevertPreviewLists preview={preview} productNames={names} />
        ) : since.length > 0 ? (
          <div className="flex flex-col gap-6">
            <span className="type-label-xs text-ink-muted">
              Undone ({since.length} change{since.length === 1 ? '' : 's'} published since)
            </span>
            <div className="max-h-[24rem] overflow-y-auto border border-line-subtle px-12 py-10">
              <ChangeList
                net
                productNames={names}
                changes={since.map((e) => ({ field: e.field ?? '', old: e.old_value ?? null, new: e.new_value ?? null }))}
              />
            </div>
          </div>
        ) : (
          <p className="type-body-sm text-ink-muted">
            The history lists no field changes since that version.
          </p>
        )}
        <label className="flex flex-col gap-6">
          <span className="type-label-xs text-ink-muted">Reason (optional, kept in History)</span>
          <textarea className={textareaClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
        </label>
      </ConfirmDialog>
    </>
  );
}

/** The revert dialog's two lists (`revert_preview`): what goes back to the
 *  approved version, and the product, price and promo edits a revert leaves
 *  live (they stay "edited since review" until a store approval or "Mark
 *  as reviewed"). */
function RevertPreviewLists({
  preview,
  productNames,
}: {
  preview: NonNullable<AdminStorefrontDetail['revert_preview']>;
  productNames: Record<string, string>;
}) {
  return (
    <div className="flex flex-col gap-14">
      <div className="flex flex-col gap-6">
        <span className="type-label-xs text-ink-muted">
          Restores ({preview.restores.length} field{preview.restores.length === 1 ? '' : 's'}: live
          now → approved version)
        </span>
        {preview.restores.length > 0 ? (
          <div className="max-h-[22rem] overflow-y-auto border border-line-subtle px-12 py-10">
            <ChangeList productNames={productNames} changes={preview.restores} />
          </div>
        ) : (
          <p className="type-body-sm text-ink-muted">
            The live copy, images and design already match that version.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-6">
        <span className="type-label-xs text-ink-muted">
          Stays live: not part of a version ({preview.not_restored.length})
        </span>
        {preview.not_restored.length > 0 ? (
          <>
            <div className="max-h-[16rem] overflow-y-auto border border-warning bg-warning-wash px-12 py-10">
              <ChangeList
                net
                productNames={productNames}
                changes={[...preview.not_restored]
                  .sort((a, b) => (Date.parse(a.created_at ?? '') || 0) - (Date.parse(b.created_at ?? '') || 0))
                  .map((e) => ({ field: e.field, old: e.old_value ?? null, new: e.new_value ?? null }))}
              />
            </div>
            <span className="type-body-sm text-ink-secondary leading-130">
              Product, price and promo code edits since that version stay as they are and stay
              &ldquo;edited since review&rdquo; until you approve the store or mark it reviewed. Ask
              the owner to change them if they should go.
            </span>
          </>
        ) : (
          <p className="type-body-sm text-ink-muted">
            No product, price or promo code edits since that version.
          </p>
        )}
      </div>
    </div>
  );
}

/** Published snapshots, newest first. */
export function VersionsPanel({ store }: { store: AdminStorefrontDetail }) {
  const versions = [...(store.versions ?? [])].sort((a, b) => b.version - a.version);
  if (versions.length === 0) return null;
  const { target } = revertTarget(store);
  const names = productNameMap(store);
  return (
    <Panel
      title="Published versions"
      aside={<History size={14} className="text-ink-muted" aria-hidden />}
      bodyClassName="p-0"
    >
      <ol className="flex flex-col">
        {versions.slice(0, 8).map((v) => (
          <li
            key={v.id}
            className="flex items-start justify-between gap-10 px-16 py-10 border-b border-line-subtle last:border-b-0"
          >
            <span className="flex flex-col gap-2 min-w-0">
              <span className="type-body-sm text-ink">
                v{v.version} · {VERSION_LABEL[v.kind] ?? v.kind}
              </span>
              <span className="type-body-sm text-ink-muted truncate" title={v.actor ?? undefined}>
                {dateTime(v.created_at)}
                {v.actor ? ` · ${v.actor}` : ''}
              </span>
              {v.note && (
                <span className="type-body-sm text-ink-secondary break-words">
                  {v.kind === 'published' ? readableNote(v.note, names) : v.note}
                </span>
              )}
            </span>
            {target?.id === v.id && (
              <Pill tone="success" className="normal-case shrink-0">
                Revert target
              </Pill>
            )}
          </li>
        ))}
      </ol>
    </Panel>
  );
}
